import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getProfile, updateProfile, completeOnboarding, listSkills, getConnections, syncFromGitHub } from '../api'

const STEPS = ['Welcome', 'Connect GitHub', 'Your first skill', 'Choose platforms', 'Invite your team', 'Done']
const PROGRESS = [16, 32, 48, 64, 80, 100]

const CheckSvg = ({ size = 12, stroke = 3 }: { size?: number; stroke?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
)

const ArrowSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
)

interface Skill { id: string; name: string; description?: string; status: string; version: string }

export default function OnboardingPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [step, setStep] = useState(1)
  const [name, setName] = useState('')
  const [workspace, setWorkspace] = useState('')
  const [role, setRole] = useState('Marketing professional')
  const [ghConnected, setGhConnected] = useState(false)
  const [ghRepo, setGhRepo] = useState('')
  const [skills, setSkills] = useState<Skill[]>([])
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null)
  const [platforms, setPlatforms] = useState<Set<string>>(new Set(['Claude']))
  const [inviteEmails, setInviteEmails] = useState(['', ''])
  const [syncing, setSyncing] = useState(false)

  useEffect(() => {
    // Load profile
    getProfile().then(r => {
      setName(r.data.display_name || '')
      setWorkspace(r.data.workspace_name || '')
      if (r.data.onboarding_completed) navigate('/', { replace: true })
    }).catch(() => {})

    // Check GitHub connection
    getConnections().then(r => {
      const gh = (r.data || []).find((c: any) => c.type === 'github')
      if (gh?.config?.repo) {
        setGhConnected(true)
        setGhRepo(gh.config.repo)
      }
    }).catch(() => {})

    // Load skills
    listSkills().then(r => {
      const s = Array.isArray(r.data) ? r.data : (r.data?.data ?? [])
      setSkills(s)
      if (s.length > 0) setSelectedSkill(s[0].name)
    }).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const goTo = (n: number) => setStep(n)

  const nextStep = async () => {
    if (step === 1) {
      // Save profile
      if (name || workspace) {
        try { await updateProfile({ display_name: name, workspace_name: workspace }) } catch {}
      }
    }
    if (step < 6) setStep(step + 1)
  }

  const finish = async () => {
    try { await completeOnboarding() } catch {}
    navigate('/', { replace: true })
  }

  const skip = async () => {
    try { await completeOnboarding() } catch {}
    navigate('/', { replace: true })
  }

  const togglePlatform = (p: string) => {
    setPlatforms(prev => {
      const next = new Set(prev)
      if (next.has(p)) next.delete(p); else next.add(p)
      return next
    })
  }

  const topSkills = skills.slice(0, 3)

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--bg)', color: 'var(--text1)' }}>
      {/* Nav */}
      <nav className="h-[52px] flex items-center px-6 gap-[10px] flex-shrink-0" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <svg width="24" height="24" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
          <rect width="28" height="28" rx="6" fill="var(--mark-bg)"/>
          <g transform="rotate(-10,14,14)">
            <rect x="5" y="7" width="18" height="3.5" rx="1.75" fill="var(--mark-tile)" opacity="1"/>
            <rect x="5" y="12.5" width="14" height="3.5" rx="1.75" fill="var(--mark-tile)" opacity="0.6"/>
            <rect x="5" y="18" width="10" height="3.5" rx="1.75" fill="var(--mark-tile)" opacity="0.3"/>
          </g>
        </svg>
        <span className="text-[15px] font-bold tracking-[-0.3px]" style={{ color: 'var(--text1)' }}>
          Skil<span style={{ color: 'var(--flow-color)' }}>flow</span>
        </span>
        <button onClick={skip} className="ml-auto text-[12px] cursor-pointer bg-transparent border-none rounded-[6px] px-2 py-1" style={{ color: 'var(--text3)', fontFamily: 'inherit' }}>
          Skip setup →
        </button>
      </nav>

      {/* Main */}
      <div className="flex-1 flex items-start justify-center px-6 pt-12 pb-20 gap-12">
        {/* Sidebar */}
        <div className="w-[200px] flex-shrink-0 pt-2 hidden md:block">
          <div className="text-[10px] font-bold uppercase tracking-[0.8px] mb-4" style={{ color: 'var(--text3)' }}>Setup progress</div>
          {STEPS.map((label, i) => {
            const n = i + 1
            const isDone = n < step
            const isActive = n === step
            return (
              <React.Fragment key={n}>
                <div className="flex items-center gap-[10px] py-2 cursor-pointer" onClick={() => n <= step && goTo(n)}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold transition-all duration-200"
                    style={{
                      border: `2px solid ${isDone ? 'var(--brand)' : isActive ? 'var(--brand)' : 'var(--border)'}`,
                      background: isDone ? 'var(--brand)' : isActive ? 'var(--brand-dim)' : 'var(--surface)',
                      color: isDone ? '#fff' : isActive ? 'var(--brand-light)' : 'var(--text3)',
                    }}>
                    {isDone ? <CheckSvg size={12} stroke={3} /> : n}
                  </div>
                  <span className="text-[13px] font-medium" style={{ color: isDone ? 'var(--text2)' : isActive ? 'var(--text1)' : 'var(--text3)' }}>{label}</span>
                </div>
                {n < 6 && <div className="w-[2px] h-4 ml-[13px] transition-colors duration-300" style={{ background: isDone ? 'var(--brand)' : 'var(--border)' }} />}
              </React.Fragment>
            )
          })}
        </div>

        {/* Content */}
        <div className="flex-1 max-w-[560px]">
          {/* Progress bar */}
          <div className="h-[3px] rounded-[2px] mb-7 overflow-hidden" style={{ background: 'var(--border)' }}>
            <div className="h-full rounded-[2px] transition-[width] duration-400" style={{ background: 'var(--brand)', width: `${PROGRESS[step - 1]}%` }} />
          </div>

          {/* Step 1: Welcome */}
          {step === 1 && (
            <Card eyebrow="Step 1 of 5" title="Welcome to Skilflow" sub="Let's set up your AI skill library. This takes about 3 minutes.">
              <Field label="Your name"><Input value={name} onChange={setName} placeholder="Your name" /></Field>
              <Field label="Workspace name"><Input value={workspace} onChange={setWorkspace} placeholder="My Team" /></Field>
              <Field label="What best describes you?">
                <select value={role} onChange={e => setRole(e.target.value)}
                  className="w-full py-[10px] px-[14px] rounded-lg text-[14px] outline-none"
                  style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text1)', fontFamily: 'inherit' }}>
                  {['Marketing professional', 'Content writer', 'Sales professional', 'Product manager', 'Developer', 'Other'].map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <BtnRow><BtnPrimary onClick={nextStep}>Get started <ArrowSvg /></BtnPrimary></BtnRow>
            </Card>
          )}

          {/* Step 2: Connect GitHub */}
          {step === 2 && (
            <Card eyebrow="Step 2 of 5" title="Connect your skill library" sub="Your skills live in a GitHub repo. Connect it and we'll sync your existing skills.">
              {ghConnected ? (
                <>
                  <div className="flex items-center gap-3 rounded-[10px] p-[14px] mb-5" style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.25)' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--brand)' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <div>
                      <div className="text-[13px] font-medium" style={{ color: 'var(--brand-light)' }}>{ghRepo} connected</div>
                      <div className="text-[12px] mt-0.5" style={{ color: 'var(--text2)' }}>{skills.length} skills found</div>
                    </div>
                  </div>
                  {topSkills.length > 0 && (
                    <div className="rounded-[10px] p-4 mb-5" style={{ background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-2 mb-2.5">
                        <span className="px-2 py-[3px] rounded-[5px] text-[10px] font-bold" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>✓ Production</span>
                        <span className="px-[7px] py-[3px] rounded-lg text-[10px] font-bold" style={{ background: 'var(--brand-dim)', color: 'var(--brand-light)' }}>C</span>
                      </div>
                      <div className="text-[14px] font-semibold mb-1" style={{ color: 'var(--text1)' }}>{topSkills[0].name}</div>
                      <div className="text-[12px] leading-[1.5]" style={{ color: 'var(--text2)' }}>{topSkills[0].description || 'No description'}</div>
                      {skills.length > 1 && <div className="text-[12px] mt-2.5 pt-2.5" style={{ color: 'var(--text3)', borderTop: '1px solid var(--border)' }}>+ {skills.length - 1} more skills synced</div>}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-[13px] mb-5" style={{ color: 'var(--text2)' }}>
                  No GitHub repo connected yet. You can connect one in{' '}
                  <button onClick={() => navigate('/settings')} className="underline cursor-pointer bg-transparent border-none" style={{ color: 'var(--brand-light)', fontFamily: 'inherit', fontSize: '13px' }}>Settings</button>
                  {' '}or skip this step.
                </div>
              )}
              <BtnRow>
                <BtnPrimary onClick={nextStep}>{ghConnected ? 'Looks good' : 'Continue'} <ArrowSvg /></BtnPrimary>
                {!ghConnected && <BtnGhost onClick={nextStep}>Skip</BtnGhost>}
              </BtnRow>
            </Card>
          )}

          {/* Step 3: First skill */}
          {step === 3 && (
            <Card eyebrow="Step 3 of 5" title="Set your first skill as active" sub="Pick one skill to promote to production. This is what your team will use immediately.">
              <div className="grid grid-cols-2 gap-2.5 mb-5">
                {topSkills.map(s => (
                  <OptionTile key={s.name} name={s.name} desc={s.description || s.status} selected={selectedSkill === s.name} onClick={() => setSelectedSkill(s.name)} />
                ))}
                <OptionTile name="I'll choose later" desc="Set up from the dashboard" selected={selectedSkill === null} onClick={() => setSelectedSkill(null)} />
              </div>
              <BtnRow><BtnPrimary onClick={nextStep}>{selectedSkill ? 'Set as active' : 'Continue'} <ArrowSvg /></BtnPrimary></BtnRow>
            </Card>
          )}

          {/* Step 4: Platforms */}
          {step === 4 && (
            <Card eyebrow="Step 4 of 5" title="Where do you use AI?" sub="Select the platforms you want to publish skills to. You can add more later.">
              <div className="grid grid-cols-3 gap-2.5 mb-5">
                {[
                  { name: 'Claude', desc: 'claude.ai + Desktop' },
                  { name: 'Open WebUI', desc: 'Self-hosted LLMs' },
                  { name: 'ChatGPT', desc: 'GPT-4 and beyond' },
                  { name: 'Copilot', desc: 'Microsoft 365' },
                  { name: 'Gemini', desc: 'Google Workspace' },
                  { name: 'API only', desc: 'Direct integration' },
                ].map(p => (
                  <OptionTile key={p.name} name={p.name} desc={p.desc} selected={platforms.has(p.name)} onClick={() => togglePlatform(p.name)} />
                ))}
              </div>
              <BtnRow>
                <BtnPrimary onClick={nextStep}>Continue <ArrowSvg /></BtnPrimary>
                <BtnGhost onClick={nextStep}>Skip</BtnGhost>
              </BtnRow>
            </Card>
          )}

          {/* Step 5: Invite */}
          {step === 5 && (
            <Card eyebrow="Step 5 of 5" title="Invite your team" sub="Skills are more powerful when shared. Invite teammates to your workspace.">
              {inviteEmails.map((email, i) => (
                <div key={i} className="flex gap-2.5 mb-2">
                  <Input value={email} onChange={v => { const e = [...inviteEmails]; e[i] = v; setInviteEmails(e) }} placeholder="teammate@company.com" />
                  <button className="py-[10px] px-4 rounded-lg text-[13px] font-semibold cursor-pointer whitespace-nowrap"
                    style={{ border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', fontFamily: 'inherit' }}>
                    Invite
                  </button>
                </div>
              ))}
              <div className="text-[12px] mt-4 mb-3" style={{ color: 'var(--text3)' }}>Or share your workspace link</div>
              <div className="flex items-center gap-2 rounded-lg p-[10px] mb-6" style={{ background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                <span className="text-[13px] flex-1 font-mono" style={{ color: 'var(--text2)' }}>app.skilflow.ai/join/{(workspace || 'my-team').toLowerCase().replace(/\s+/g, '-')}</span>
                <button onClick={e => { navigator.clipboard.writeText(`app.skilflow.ai/join/${(workspace || 'my-team').toLowerCase().replace(/\s+/g, '-')}`); (e.target as HTMLButtonElement).textContent = 'Copied!' }}
                  className="px-2.5 py-1 rounded-[6px] text-[12px] cursor-pointer" style={{ border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', fontFamily: 'inherit' }}>
                  Copy
                </button>
              </div>
              <BtnRow>
                <BtnPrimary onClick={() => setStep(6)}>Finish setup <CheckSvg size={14} stroke={2.5} /></BtnPrimary>
                <BtnGhost onClick={() => setStep(6)}>Skip for now</BtnGhost>
              </BtnRow>
            </Card>
          )}

          {/* Step 6: Done */}
          {step === 6 && (
            <div className="rounded-2xl p-12 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="w-[72px] h-[72px] rounded-[20px] flex items-center justify-center mx-auto mb-6" style={{ background: 'var(--brand)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div className="text-[28px] font-extrabold tracking-[-0.8px] mb-2.5">You're all set{name ? `, ${name.split(' ')[0]}` : ''}</div>
              <div className="text-[15px] leading-[1.6] max-w-[420px] mx-auto mb-8" style={{ color: 'var(--text2)' }}>
                Your skill library is live. Your team can now use consistent, versioned AI skills across every platform.
              </div>
              <div className="flex gap-4 justify-center mb-8 flex-wrap">
                <StatBox value={skills.length} label="Skills synced" />
                <StatBox value={platforms.size} label="Platforms selected" />
                <StatBox value={selectedSkill ? 1 : 0} label="Skill in production" />
              </div>
              <div className="rounded-xl p-5 mb-7 text-left" style={{ background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                <div className="text-[12px] font-bold uppercase tracking-[0.7px] mb-3.5" style={{ color: 'var(--text3)' }}>Setup complete</div>
                <div className="flex flex-col gap-2.5">
                  <DoneRow>Workspace created{workspace ? ` · ${workspace}` : ''}</DoneRow>
                  {ghConnected && <DoneRow>GitHub connected · {skills.length} skills synced</DoneRow>}
                  {selectedSkill && <DoneRow>{selectedSkill} set to production</DoneRow>}
                  {platforms.size > 0 && <DoneRow>{Array.from(platforms).join(' + ')} selected</DoneRow>}
                </div>
              </div>
              <button onClick={finish} className="inline-flex items-center gap-[7px] py-[13px] px-7 rounded-[9px] text-[15px] font-semibold text-white cursor-pointer border-none"
                style={{ background: 'var(--brand)', fontFamily: 'inherit' }}>
                Go to your dashboard <ArrowSvg />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Shared components ──

function Card({ eyebrow, title, sub, children }: { eyebrow: string; title: string; sub: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl p-8" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="text-[11px] font-bold uppercase tracking-[0.8px] mb-2" style={{ color: 'var(--brand-light)' }}>{eyebrow}</div>
      <div className="text-[22px] font-bold tracking-[-0.5px] mb-2 leading-[1.3]" style={{ color: 'var(--text1)' }}>{title}</div>
      <div className="text-[14px] leading-[1.6] mb-7" style={{ color: 'var(--text2)' }}>{sub}</div>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <label className="block text-[12px] font-semibold uppercase tracking-[0.5px] mb-1.5" style={{ color: 'var(--text2)' }}>{label}</label>
      {children}
    </div>
  )
}

function Input({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full py-[10px] px-[14px] rounded-lg text-[14px] outline-none transition-colors"
      style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text1)', fontFamily: 'inherit' }}
      onFocus={e => e.target.style.borderColor = 'var(--brand)'}
      onBlur={e => e.target.style.borderColor = 'var(--border)'} />
  )
}

function OptionTile({ name, desc, selected, onClick }: { name: string; desc: string; selected: boolean; onClick: () => void }) {
  return (
    <div onClick={onClick} className="p-4 rounded-[10px] cursor-pointer transition-all duration-150"
      style={{
        background: selected ? 'var(--brand-dim)' : 'var(--surface2)',
        border: `1px solid ${selected ? 'var(--brand)' : 'var(--border)'}`,
      }}>
      <div className="text-[13px] font-semibold mb-0.5" style={{ color: 'var(--text1)' }}>{name}</div>
      <div className="text-[11px] leading-[1.4]" style={{ color: 'var(--text2)' }}>{desc}</div>
    </div>
  )
}

function BtnRow({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-2.5 mt-2">{children}</div>
}

function BtnPrimary({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-[7px] py-[11px] px-6 rounded-[9px] text-[14px] font-semibold text-white cursor-pointer border-none transition-all duration-150 hover:-translate-y-px"
      style={{ background: 'var(--brand)', fontFamily: 'inherit' }}>
      {children}
    </button>
  )
}

function BtnGhost({ onClick, children }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick} className="py-[11px] px-[18px] rounded-[9px] text-[14px] font-medium cursor-pointer transition-all duration-150"
      style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', fontFamily: 'inherit' }}>
      {children}
    </button>
  )
}

function StatBox({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-[10px] py-3.5 px-5 text-center min-w-[100px]" style={{ background: 'var(--surface2)', border: '1px solid var(--border)' }}>
      <div className="text-[22px] font-bold" style={{ color: 'var(--brand-light)' }}>{value}</div>
      <div className="text-[11px] mt-0.5" style={{ color: 'var(--text3)' }}>{label}</div>
    </div>
  )
}

function DoneRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 text-[13px]" style={{ color: 'var(--text2)' }}>
      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--brand)' }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      {children}
    </div>
  )
}
